import ReportingRequest from '@/Enums/Attribution/WebVisits/ReportingRequest.enum';
import { LOOKER_REPORT_DEBOUNCE_MS } from '@/constants/beeswax/asyncReport';
import { batchActions } from '@/helpers/reporting/batching.helpers';
import { IRequestSaverFunctions } from '@/hooks/RequestSaver';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectWebVisitsFilterSourceLoading } from '@/store/selectors/attribution/webVisits.selectors';
import {
  getWebVisitsCampaignNameData,
  getWebVisitsCityData,
  getWebVisitsCongressionalDistrictsData,
  getWebVisitsCountyData,
  getWebVisitsCreativeData,
  getWebVisitsDateData,
  getWebVisitsDayOfWeekData,
  getWebVisitsDaypartData,
  getWebVisitsDeviceData,
  getWebVisitsDigitalChannelData,
  getWebVisitsDmaData,
  getWebVisitsPixelData,
  getWebVisitsPlacementData,
  getWebVisitsPublisherNameData,
  getWebVisitsStateData,
  getWebVisitsZipData,
} from '@/store/slices/attribution/beeswaxThunks';
import { resetWebVisitsPageState } from '@/store/slices/attribution/webVisits.slice';
import { useCallback, useEffect, useMemo } from 'react';

export const useWebVisitsReporting = (requestSaver: IRequestSaverFunctions) => {
  const dispatch = useAppDispatch();
  const loadingFilterSource = useAppSelector(selectWebVisitsFilterSourceLoading);

  const wrapThunk = useCallback(
    (thunk: any, request: ReportingRequest) => async () => {
      await dispatch(thunk(requestSaver.requestSaverSetCancellation(request)));
    },
    [dispatch, requestSaver],
  );

  const firstBatch = useMemo(
    () => [
      wrapThunk(getWebVisitsDateData, ReportingRequest.Date),
      wrapThunk(getWebVisitsCampaignNameData, ReportingRequest.Campaign),
      wrapThunk(getWebVisitsPixelData, ReportingRequest.Pixel),
    ],
    [wrapThunk],
  );

  const secondBatch = useMemo(
    () => [
      wrapThunk(getWebVisitsDeviceData, ReportingRequest.Device),
      wrapThunk(getWebVisitsDigitalChannelData, ReportingRequest.Channel),
      wrapThunk(getWebVisitsPlacementData, ReportingRequest.Placement),
      wrapThunk(getWebVisitsCreativeData, ReportingRequest.Creative),
    ],
    [wrapThunk],
  );

  const thirdBatch = useMemo(
    () => [
      wrapThunk(getWebVisitsPublisherNameData, ReportingRequest.Publisher),
      wrapThunk(getWebVisitsDayOfWeekData, ReportingRequest.DayOfWeek),
      wrapThunk(getWebVisitsDaypartData, ReportingRequest.Daypart),
    ],
    [wrapThunk],
  );

  const fourthBatch = useMemo(
    () => [
      wrapThunk(getWebVisitsStateData, ReportingRequest.State),
      wrapThunk(getWebVisitsDmaData, ReportingRequest.Dma),
      wrapThunk(getWebVisitsZipData, ReportingRequest.Zip),
      wrapThunk(getWebVisitsCongressionalDistrictsData, ReportingRequest.Congressional),
      wrapThunk(getWebVisitsCountyData, ReportingRequest.County),
      wrapThunk(getWebVisitsCityData, ReportingRequest.City),
    ],
    [wrapThunk],
  );

  const runBatchesSequentially = useCallback(async () => {
    await batchActions(firstBatch, LOOKER_REPORT_DEBOUNCE_MS);
    await batchActions(secondBatch, LOOKER_REPORT_DEBOUNCE_MS);
    await batchActions(thirdBatch, LOOKER_REPORT_DEBOUNCE_MS);
    await batchActions(fourthBatch, LOOKER_REPORT_DEBOUNCE_MS);
  }, [firstBatch, secondBatch, thirdBatch, fourthBatch]);

  const loadReports = useCallback(async () => {
    requestSaver.requestSaverAbortAll();
    await runBatchesSequentially();
  }, [requestSaver, runBatchesSequentially]);

  useEffect(() => {
    return () => {
      requestSaver.requestSaverAbortAll();
      dispatch(resetWebVisitsPageState());
    };
  }, [dispatch, requestSaver]);

  const applyFilters = useCallback(async () => {
    if (loadingFilterSource) return;
    await loadReports();
  }, [loadingFilterSource, loadReports]);

  return {
    applyFilters,
    loadReports,
    loadingFilterSource,
  };
};
